// Environment variables loaded from .env
import { withAuthHeaders } from "./auth";

const BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
const api = (path) => `${BASE}/${String(path).replace(/^\/+/, "")}`;

async function parseResponse(response, defaultMessage) {
  let data = null;
  try {
    data = await response.json();
  } catch {
    data = null;
  }

  if (!response.ok) {
    const message = data?.message || data?.error || defaultMessage;
    const error = new Error(message);
    error.status = response.status;
    throw error;
  }

  return data;
}

async function request(path, options = {}, defaultMessage = "Subscription request failed") {
  const response = await fetch(api(path), {
    ...options,
    headers: withAuthHeaders(options.headers),
  });
  return parseResponse(response, defaultMessage);
}

/**
 * Maps backend payload shapes to the SubscriptionContext contract.
 * Supports snake_case and camelCase field names.
 */
export function normalizeSubscriptionPayload(payload) {
  if (!payload || typeof payload !== "object") return null;

  const raw = payload.subscription || payload.data || payload;

  const planType = raw.planType ?? raw.plan_type ?? raw.plan ?? "monthly";
  const includesEhr = Boolean(
    raw.includesEhr ?? raw.includes_ehr ?? raw.hasEhr ?? raw.has_ehr
  );

  const resolvedPlan =
    planType === "monthlyWithEhr" || planType === "monthly_with_ehr"
      ? "monthlyWithEhr"
      : planType === "monthly" || planType === "individual"
        ? includesEhr
          ? "monthlyWithEhr"
          : "monthly"
        : includesEhr
          ? "monthlyWithEhr"
          : "monthly";

  const currentSeats = Number(
    raw.currentSeats ?? raw.current_seats ?? raw.seatsUsed ?? raw.seats_used ?? 1
  );
  const maxSeats = Number(
    raw.maxSeats ?? raw.max_seats ?? raw.seatLimit ?? raw.seat_limit ?? 2
  );

  return {
    planType: resolvedPlan,
    currentSeats: Number.isFinite(currentSeats) ? currentSeats : 1,
    maxSeats: Number.isFinite(maxSeats) ? maxSeats : 2,
    isTrial: Boolean(raw.isTrial ?? raw.is_trial ?? raw.trialActive ?? raw.trial_active),
    trialEndDate:
      raw.trialEndDate ?? raw.trial_end_date ?? raw.trialEndsAt ?? raw.trial_ends_at ?? null,
    subscriptionEndDate:
      raw.subscriptionEndDate ??
      raw.subscription_end_date ??
      raw.expiresAt ??
      raw.expires_at ??
      null,
    paymentFailed: Boolean(
      raw.paymentFailed ?? raw.payment_failed ?? raw.lastPaymentFailed ?? raw.last_payment_failed
    ),
  };
}

/**
 * GET current clinic subscription status (trial, seats, payment state).
 * Backend route expected: GET /api/subscriptions/me
 */
export async function fetchSubscriptionStatus() {
  return request("/api/subscriptions/me", { method: "GET" }, "Failed to load subscription");
}

/**
 * POST payment for selected plan (used by PaymentBilling).
 * Backend route expected: POST /api/subscriptions/payment
 */
export async function submitSubscriptionPayment(payload) {
  return request(
    "/api/subscriptions/payment",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    },
    "Payment failed"
  );
}

/**
 * POST checkout session for clinic upgrade (used by MaxSeatModal).
 * Backend route expected: POST /api/subscriptions/checkout-session
 */
export async function createCheckoutSession(payload = { targetPlan: "clinic" }) {
  return request(
    "/api/subscriptions/checkout-session",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    },
    "Failed to start checkout"
  );
}
